import * as Sentry from '@sentry/react';
import { useMemo } from 'react';

export interface LogContext {
  userId?: string;
  locationId?: string;
  locationName?: string;
  operation?: string;
  sku?: string;
  orderId?: string;
  duration?: number;
  itemCount?: number;
  error?: string;
  businessImpact?: string;
  [key: string]: unknown;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment = import.meta.env.MODE === 'development';

  private log(level: LogLevel, message: string, context?: LogContext) {
    // Always log to console in development
    if (this.isDevelopment) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, context || '');
    }

    // Send to Sentry based on level
    if (level === 'error') {
      Sentry.captureException(new Error(message), {
        extra: context,
        level: 'error',
      });
    } else if (level === 'warn') {
      Sentry.captureMessage(message, {
        level: 'warning',
        extra: context,
      });
    } else {
      // For info and debug, add as breadcrumb
      Sentry.addBreadcrumb({
        message,
        level: level === 'info' ? 'info' : 'debug',
        data: context,
        category: 'business-event',
      });
    }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, context?: LogContext) {
    this.log('error', message, context);
  }

  stockOperation(operation: string, success: boolean, context: LogContext) {
    const message = `Stock ${operation} ${success ? 'successful' : 'failed'}`;
    const level = success ? 'info' : 'error';

    this.log(level, message, {
      ...context,
      operation: `stock_${operation}`,
      businessImpact: success ? 'inventory_updated' : 'inventory_operation_failed',
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Custom hook for component-level logging
export const useLogger = () => {
  return useMemo(() => ({
    debug: logger.debug.bind(logger),
    info: logger.info.bind(logger),
    warn: logger.warn.bind(logger),
    error: logger.error.bind(logger),
    stockOperation: logger.stockOperation.bind(logger),
  }), []);
};
